import {
  type EditableStoredPaymentSchedule,
  type EditableStoredPaymentScheduleStage,
} from '@payaca/helpers/editableStoredPaymentSchedule';
import {
  isFixedDepositConvertible,
  isPaymentScheduleRebuildRequired,
} from '@payaca/helpers/paymentScheduleHelper';
import { isFixedDepositStage } from '@payaca/helpers/storedPaymentSchedule';
import {
  type PaymentScheduleExplainer,
  type StoredPaymentScheduleStage,
} from '@payaca/types/payment-schedule';

export type ChangeProposalPaymentScheduleRow = {
  stage: StoredPaymentScheduleStage | null;
  invoice?: {
    reference: number;
    customReference?: string | null;
    isDepositInvoice: boolean;
  } | null;
  /** In minor units. */
  invoiceValue?: number | null;
  invoiceProjectPercentage?: number | null;
};

const isShown = (isInSync: boolean, row: ChangeProposalPaymentScheduleRow) =>
  isInSync ? !!row.stage : !!row.invoice;

type Invoice = NonNullable<ChangeProposalPaymentScheduleRow['invoice']>;

const invoiceLabel = (invoice: Invoice) =>
  `Invoice #${invoice.customReference || invoice.reference}`;

const describeInvoicedStage = (args: {
  stage: StoredPaymentScheduleStage | null;
  invoice: Invoice;
  stageIndex: number;
  isRebuildRequired: boolean;
}) => {
  const { stage, invoice, stageIndex, isRebuildRequired } = args;
  if (isRebuildRequired) {
    return invoice.isDepositInvoice ? 'Deposit' : `Stage ${stageIndex + 1}`;
  }
  return stage?.description ?? invoiceLabel(invoice);
};

/**
 * In sync, the editor shows the stored stages. Out of sync, it shows only what
 * has been invoiced, under the stages' names while the invoices still match
 * them. An invoiced fixed deposit followed by an invoiced instalment becomes a
 * percentage deposit at its invoice's share.
 */
export const toEditableChangeProposalPaymentSchedule = (args: {
  isInSync: boolean;
  rows: ChangeProposalPaymentScheduleRow[];
  explainer?: PaymentScheduleExplainer;
}): EditableStoredPaymentSchedule => {
  const { isInSync, rows, explainer } = args;

  if (isInSync) {
    return {
      stages: rows.flatMap(({ stage }) => (stage ? [stage] : [])),
      explainer,
    };
  }

  const isRebuildRequired = isPaymentScheduleRebuildRequired({
    isInSync,
    rows,
  });
  const keepsFixedDeposit =
    !isRebuildRequired && !isFixedDepositConvertible({ isInSync, rows });

  return {
    stages: rows
      .filter((row) => row.invoice)
      .flatMap(
        (
          { stage, invoice, invoiceProjectPercentage },
          stageIndex
        ): EditableStoredPaymentScheduleStage[] => {
          if (!invoice) return [];
          if (
            keepsFixedDeposit &&
            stage &&
            isFixedDepositStage(stage) &&
            invoice.isDepositInvoice
          ) {
            return [stage];
          }

          const description = describeInvoicedStage({
            stage,
            invoice,
            stageIndex,
            isRebuildRequired,
          });
          const percentageDue = invoiceProjectPercentage ?? 0;
          // Locked (invoice-backed) rows must reflect invoice reality, not
          // the saved schedule.
          return [
            invoice.isDepositInvoice
              ? {
                  type: 'deposit',
                  description,
                  basis: { type: 'percentage', percentageDue },
                }
              : { type: 'instalment', description, percentageDue },
          ];
        }
      ),
    explainer,
  };
};

export type InvoicedStage = {
  description: string;
  invoiceLabel: string;
  /** In minor units. */
  value: number;
  /** The stage's position in the editable schedule. */
  stageIndex: number;
};

/**
 * The stages already invoiced, in schedule order. They aren't always the first
 * stages: a stage a fixed deposit uses up has no invoice of its own.
 */
export const getInvoicedStages = (args: {
  isInSync: boolean;
  rows: ChangeProposalPaymentScheduleRow[];
}): InvoicedStage[] => {
  const { isInSync, rows } = args;
  const isRebuildRequired = isPaymentScheduleRebuildRequired({
    isInSync,
    rows,
  });

  return rows
    .filter((row) => isShown(isInSync, row))
    .flatMap(({ stage, invoice, invoiceValue }, stageIndex) =>
      invoice
        ? [
            {
              description: describeInvoicedStage({
                stage,
                invoice,
                stageIndex,
                isRebuildRequired,
              }),
              invoiceLabel: invoiceLabel(invoice),
              value: invoiceValue ?? 0,
              stageIndex,
            },
          ]
        : []
    );
};
